// What a "request update" (POST .../request-location) came to, for the member
// sidebar. Pure, so it is unit-tested.

/** The server allows one request per member every 60 s, whatever the outcome of the dispatch. */
export const REQUEST_COOLDOWN_MS = 60_000;

export type RequestOutcome =
  | { kind: "sent" }
  /** No phone with push registered (or only invalid registrations). */
  | { kind: "no_devices" }
  /** Someone asked recently; retry after `retryAfterMs`. */
  | { kind: "cooldown"; retryAfterMs: number }
  | { kind: "paused" }
  /** Push isn't configured on the server, or the provider failed. */
  | { kind: "unavailable" }
  /** The member or circle is gone, or the caller lost access. */
  | { kind: "gone" }
  | { kind: "error"; message: string };

/** Classifies a response: its status, the 202 body when there is one, and `Retry-After`. */
export function requestOutcome(
  status: number,
  accepted: { devices_notified: number } | undefined,
  retryAfter: string | null,
): RequestOutcome {
  if (status === 202 && accepted) return accepted.devices_notified > 0 ? { kind: "sent" } : { kind: "no_devices" };
  if (status === 429) {
    const seconds = Number(retryAfter);
    const retryAfterMs =
      retryAfter !== null && Number.isFinite(seconds) && seconds >= 0 ? seconds * 1_000 : REQUEST_COOLDOWN_MS;
    return { kind: "cooldown", retryAfterMs: Math.min(retryAfterMs, REQUEST_COOLDOWN_MS) };
  }
  if (status === 403) return { kind: "paused" };
  if (status === 404) return { kind: "gone" };
  if (status === 503) return { kind: "unavailable" };
  return { kind: "error", message: `Request failed (HTTP ${status})` };
}

/** How long the button stays disabled after `outcome`; 0 lets the user retry at once. */
export function cooldownMs(outcome: RequestOutcome): number {
  switch (outcome.kind) {
    case "sent":
    case "no_devices":
    case "unavailable":
      return REQUEST_COOLDOWN_MS;
    case "cooldown":
      return outcome.retryAfterMs;
    default:
      return 0;
  }
}

/** A short message about `outcome` for the member named `name`. */
export function outcomeText(outcome: RequestOutcome, name: string): string {
  switch (outcome.kind) {
    case "sent":
      return `Asked ${name}'s phone for a fresh location.`;
    case "no_devices":
      return `${name} has no phone that can be asked for a location.`;
    case "cooldown":
      return `${name} was asked recently. Try again in ${Math.max(1, Math.ceil(outcome.retryAfterMs / 1_000))} s.`;
    case "paused":
      return `${name} has paused location sharing.`;
    case "unavailable":
      return `Couldn't reach ${name}'s phone. Try again in a minute.`;
    case "gone":
      return `${name} is no longer in this circle.`;
    case "error":
      return outcome.message;
  }
}
